import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Lightbulb, ImagePlus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { t } from "../lib/i18n";

export const IM_SLIDES = [
  { key: "executive_summary", label: t("Resumen Ejecutivo") },
  { key: "market_analysis", label: t("Análisis del Mercado") },
  { key: "operations", label: t("Operaciones") },
  { key: "financial_hygiene", label: t("Fortaleza Financiera") },
];

// Reparte las imágenes por slide: las asignadas van a su slide; el resto en rotación.
export const imagesBySlide = (images = []) => {
  const out = Object.fromEntries(IM_SLIDES.map((s) => [s.key, []]));
  const auto = [];
  images.forEach((img) => (out[img.section] ? out[img.section].push(img) : auto.push(img)));
  auto.forEach((img, i) => out[IM_SLIDES[i % IM_SLIDES.length].key].push(img));
  return out;
};

export const AuthImage = ({ companyId, imageId, className = "" }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let alive = true;
    api.get(`/companies/${companyId}/im/images/${imageId}/raw`, { responseType: "blob" })
      .then((r) => {
        url = URL.createObjectURL(r.data);
        if (alive) setSrc(url);
        else URL.revokeObjectURL(url);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [companyId, imageId]);
  if (!src) return <div className={`bg-muted animate-pulse ${className}`} />;
  return <img src={src} className={`object-cover ${className}`} alt={t("Imagen corporativa")} />;
};

const FIELDS = [
  {
    key: "business_model",
    label: t("Modelo de Negocio"),
    hint: t("Aporta detalles estratégicos para atraer mejores ofertas."),
    placeholder: t("Explica cómo genera ingresos la empresa: líneas de negocio, quién paga y por qué, precios, recurrencia, contratos plurianuales… Cuanto más concreto, más convincente será tu IM."),
  },
  {
    key: "competitive_moat",
    label: t("Ventajas Competitivas (foso defensivo)"),
    hint: t("Lo que te hace difícil de replicar justifica una valoración premium."),
    placeholder: t("¿Qué te protege de la competencia? Marca reconocida, know-how propio, contratos exclusivos, tecnología, ubicación, relaciones de largo plazo, barreras regulatorias…"),
  },
  {
    key: "growth_plan",
    label: t("Plan de Crecimiento"),
    hint: t("Explica tu visión de crecimiento para validar el precio."),
    placeholder: t("¿Dónde están las palancas para el comprador? Nuevos mercados o geografías, nuevos productos, digitalización, adquisiciones, capacidad ociosa, pricing… Indica plazos y magnitudes si puedes."),
  },
];

const SECTION_OPTIONS = [{ key: "", label: t("Slide automática") }, ...IM_SLIDES];

export default function ImStrategyForm({ company, onCompanyUpdate }) {
  const companyId = company.company_id;
  const [form, setForm] = useState(company.im_strategy || {});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const images = company.im_images || [];

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/companies/${companyId}/im/strategy`, {
        business_model: form.business_model || null,
        competitive_moat: form.competitive_moat || null,
        growth_plan: form.growth_plan || null,
      });
      onCompanyUpdate(r.data.company);
      toast.success(t("Visión estratégica guardada · se usará en el próximo IM"));
    } catch {
      toast.error(t("Error guardando"));
    } finally {
      setSaving(false);
    }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    let latest = null;
    let ok = 0;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/companies/${companyId}/im/images`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        latest = r.data.company;
        ok += 1;
      }
      toast.success(ok === 1 ? "Imagen subida" : `${ok} imágenes subidas`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(
        (typeof d === "string" ? d : "Error subiendo la imagen") +
        (ok > 0 ? ` (${ok} de ${files.length} sí se subieron)` : "")
      );
    } finally {
      if (latest) onCompanyUpdate(latest);
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = async (imageId) => {
    try {
      const r = await api.delete(`/companies/${companyId}/im/images/${imageId}`);
      onCompanyUpdate(r.data.company);
      toast.success(t("Imagen eliminada"));
    } catch {
      toast.error(t("Error eliminando la imagen"));
    }
  };

  const assignSection = async (imageId, section) => {
    try {
      const r = await api.patch(`/companies/${companyId}/im/images/${imageId}`, { section: section || null });
      onCompanyUpdate(r.data.company);
    } catch {
      toast.error(t("Error asignando la slide"));
    }
  };

  return (
    <div className="hairline bg-card mb-8" data-testid="im-strategy-card">
      <div className="px-6 py-4 hairline-b bg-bone flex items-center gap-3">
        <Lightbulb className="w-4 h-4 text-gold" />
        <div>
          <span className="font-mono-label text-gold">{t("Visión Estratégica y Marca")}</span>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("Cuanta más información aportes, más sólido y persuasivo será el IM que redacte la IA. Solo lo verán NDAs aprobados.")}
          </p>
        </div>
      </div>

      <div className="p-6 grid md:grid-cols-3 gap-5">
        {FIELDS.map(({ key, label, hint, placeholder }) => (
          <div key={key}>
            <Label className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>{label}</Label>
            <p className="text-xs text-[#C25F1E] mt-1 flex items-start gap-1" data-testid={`im-strategy-hint-${key}`}>
              <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" /> {hint}
            </p>
            <Textarea
              value={form[key] || ""}
              onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
              rows={6}
              placeholder={placeholder}
              className="rounded-none mt-1.5 text-sm w-full"
              data-testid={`im-strategy-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="px-6 pb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="font-mono-label text-muted-foreground" style={{ fontSize: "0.6rem" }}>
              {t("Imágenes Corporativas · oficinas, equipo, productos (")}{images.length}/8)
            </Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("Cada imagen se integra en una slide del IM. Elige en cuál debe aparecer o déjalo en automático.")}
            </p>
          </div>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-none bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-9 px-5"
            data-testid="im-strategy-save"
          >
            {saving ? t("Guardando…") : t("Guardar visión estratégica")}
          </Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((img) => (
            <div key={img.image_id} className="hairline" data-testid={`im-image-${img.image_id}`}>
              <div className="relative group">
                <AuthImage companyId={companyId} imageId={img.image_id} className="w-full h-24" />
                <button
                  type="button"
                  onClick={() => removeImage(img.image_id)}
                  className="absolute top-1 right-1 w-6 h-6 bg-white/90 hairline flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`im-image-delete-${img.image_id}`}
                >
                  <Trash2 className="w-3 h-3 text-destructive" />
                </button>
              </div>
              <select
                value={img.section || ""}
                onChange={(e) => assignSection(img.image_id, e.target.value)}
                className="w-full h-8 text-xs bg-bone border-t border-border px-2 focus:outline-none"
                data-testid={`im-image-section-${img.image_id}`}
              >
                {SECTION_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
          ))}
          {images.length < 8 && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="h-32 hairline border-dashed flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground hover:bg-bone transition-colors"
              data-testid="im-image-upload-btn"
            >
              <ImagePlus className="w-4 h-4" />
              <span className="text-[10px] font-mono-label">{uploading ? t("Subiendo…") : t("Añadir")}</span>
            </button>
          )}
        </div>
        <input
          type="file"
          ref={fileRef}
          multiple
          accept=".png,.jpg,.jpeg,.webp"
          className="hidden"
          onChange={(e) => e.target.files?.length && uploadFiles([...e.target.files])}
          data-testid="im-image-input"
        />
      </div>
    </div>
  );
}
